import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Tabs, Tab } from '@mui/material';
import SchoolOrdersTab from './SchoolOrdersTab';
import StationeryOrdersTab from './StationeryOrdersTab';
import AdvertiserOrdersTab from './AdvertiserOrdersTab';
import UpdateOrdersTab from './UpdateOrdersTab';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`orders-tabpanel-${index}`}
      aria-labelledby={`orders-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `orders-tab-${index}`,
    'aria-controls': `orders-tabpanel-${index}`,
  };
}

function OrdersTabs({ 
  schoolOrders, 
  stationeryOrders, 
  advertiserOrders,
  updateOrders,
  loading,
  error,
  onOrderClick,
  getTotalNoteCount,
  handleOpenNotesDialog
}) {
  // Try to retrieve saved tab from localStorage, default to tab 0 (advertiser orders)
  const savedTab = localStorage.getItem('ordersActiveTab');
  const [activeTab, setActiveTab] = useState(savedTab ? parseInt(savedTab) : 0);

  // When activeTab changes, save it to localStorage
  useEffect(() => {
    localStorage.setItem('ordersActiveTab', activeTab.toString());
  }, [activeTab]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="orders tabs"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={`Advertiser Orders (${advertiserOrders.length})`} {...a11yProps(0)} />
          <Tab label={`School Orders (${schoolOrders.length})`} {...a11yProps(1)} />
          <Tab label={`Stationery Orders (${stationeryOrders.length})`} {...a11yProps(2)} />
          <Tab label={`Updates (${updateOrders ? updateOrders.length : 0})`} {...a11yProps(3)} />
        </Tabs>
      </Box>
      
      <TabPanel value={activeTab} index={0}>
        <AdvertiserOrdersTab 
          orders={advertiserOrders} 
          loading={loading} 
          error={error}
          onOrderClick={onOrderClick}
          getTotalNoteCount={getTotalNoteCount}
          handleOpenNotesDialog={handleOpenNotesDialog}
        />
      </TabPanel>
      
      <TabPanel value={activeTab} index={1}>
        <SchoolOrdersTab 
          orders={schoolOrders} 
          loading={loading} 
          error={error}
          onOrderClick={onOrderClick}
          getTotalNoteCount={getTotalNoteCount}
          handleOpenNotesDialog={handleOpenNotesDialog}
        />
      </TabPanel>
      
      <TabPanel value={activeTab} index={2}>
        <StationeryOrdersTab 
          orders={stationeryOrders} 
          loading={loading} 
          error={error}
          onOrderClick={onOrderClick}
          getTotalNoteCount={getTotalNoteCount}
          handleOpenNotesDialog={handleOpenNotesDialog}
        />
      </TabPanel>
      
      <TabPanel value={activeTab} index={3}>
        <UpdateOrdersTab 
          orders={updateOrders || []} 
          loading={loading} 
          error={error}
          onOrderClick={onOrderClick}
          getTotalNoteCount={getTotalNoteCount}
          handleOpenNotesDialog={handleOpenNotesDialog}
        />
      </TabPanel>
    </Box>
  );
}

OrdersTabs.propTypes = {
  schoolOrders: PropTypes.array.isRequired,
  stationeryOrders: PropTypes.array.isRequired,
  advertiserOrders: PropTypes.array.isRequired,
  updateOrders: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onOrderClick: PropTypes.func,
  getTotalNoteCount: PropTypes.func,
  handleOpenNotesDialog: PropTypes.func
};

OrdersTabs.defaultProps = {
  schoolOrders: [],
  stationeryOrders: [],
  advertiserOrders: [],
  updateOrders: [],
  loading: false,
  error: null
};

export default OrdersTabs; 